import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useSession } from '../hooks/session';
import { Button, Card, Spinner } from '../components/ui';
import { alert, haptic } from '../lib/telegram';

/**
 * Экран для человека без доступа. Пустой интерфейс без объяснений выглядит как
 * поломка, поэтому здесь явно сказано, что доступа нет и что с этим делать.
 */
export function NoAccessPage() {
  const session = useSession();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const pending = session.pendingAccess;
  const organizations = session.availableOrganizations;
  const waiting = sent || pending?.status === 'PENDING';

  /*
   * Пока заявка на рассмотрении, тихо перезапрашиваем сессию: доступ выдаётся
   * в чате бота, и человек не должен гадать, когда закрыть и открыть приложение.
   * Как только доступ появится, App переключит экран сам.
   */
  const refresh = useRef(session.refresh);
  refresh.current = session.refresh;

  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => void refresh.current(), 15_000);
    // Возврат из чата — самый вероятный момент, когда доступ уже выдан.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [waiting]);

  const submit = async (organizationId: string) => {
    setSending(true);
    try {
      await api.requestAccess(organizationId);
      haptic.success();
      setSent(true);
    } catch (error) {
      haptic.error();
      alert(error instanceof Error ? error.message : 'Не удалось отправить заявку');
    } finally {
      setSending(false);
    }
  };

  /* Заявка уже на рассмотрении. */
  if (waiting) {
    return (
      <Shell icon="⏳" title="Заявка отправлена">
        <p>
          Владелец организации
          {pending ? ` «${pending.organizationName}»` : ''} получил уведомление в Telegram.
        </p>
        <p className="mt-3 flex items-center justify-center gap-2">
          <Spinner size={14} />
          <span>Ждём подтверждения — экран обновится сам</span>
        </p>
      </Shell>
    );
  }

  /* Заявку отклонили. */
  if (pending?.status === 'REJECTED') {
    return (
      <Shell icon="🚫" title="Доступ не подтверждён">
        <p>
          Заявка в «{pending.organizationName}» отклонена. Если это ошибка — свяжитесь с владельцем и
          отправьте запрос снова.
        </p>
        <Button
          className="mt-5 w-full"
          loading={sending}
          onClick={() => void submit(pending.organizationId)}
        >
          Запросить доступ повторно
        </Button>
      </Shell>
    );
  }

  /* Система ещё не настроена — организаций нет. */
  if (organizations.length === 0) {
    return (
      <Shell icon="🔒" title="Система ещё не настроена">
        <p>
          Ни одна организация пока не создана. Если вы администратор — отправьте боту ключ доступа
          сообщением в чат, и здесь появится мастер настройки.
        </p>
      </Shell>
    );
  }

  /* Доступа нет, но есть куда попроситься. */
  return (
    <Shell icon="🔑" title="Нет доступа">
      <p>
        Ваш аккаунт не добавлен в организацию. Запросите доступ — владелец подтвердит его в Telegram,
        и вы сразу сможете работать с отзывами.
      </p>

      <div className="mt-5 space-y-2 text-left">
        {organizations.map((organization) => (
          <Card key={organization.id}>
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 flex-1 truncate t-body font-medium">{organization.name}</span>
              <Button loading={sending} onClick={() => void submit(organization.id)}>
                Запросить
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <p className="mt-5 t-caption leading-relaxed text-[var(--color-hint)]">
        Администратор? Отправьте боту ключ доступа сообщением — получите право создать организацию.
      </p>
    </Shell>
  );
}

function Shell({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="safe-top mx-auto flex min-h-full max-w-lg flex-col justify-center px-6 py-12 text-center">
      <div className="mb-4 text-5xl" aria-hidden="true">
        {icon}
      </div>
      <h1 className="mb-3 t-title">{title}</h1>
      <div className="t-callout leading-relaxed text-[var(--color-hint)]">{children}</div>
    </div>
  );
}
